import React, { useContext, useState, useEffect } from 'react';
import { DataContext } from 'App';
import { useNavigate, Link } from 'react-router-dom';
import { MdClose, MdNavigateNext } from 'react-icons/md';
import { HiPlus, HiMinus } from 'react-icons/hi';
import { TbRefresh } from 'react-icons/tb';
import CartService, { PKR_FREE_SHIPPING_THRESHOLD, PKR_STANDARD_SHIPPING } from '../../services/cart';
import { formatPrice } from 'utils/formatPrice';
import { t } from 'i18n';
import { getPrimaryProductImageSrc } from 'utils/productImages';
import AuthService from 'services/auth';
import { handleApiError, handleApiSuccess } from 'components/Handle';
import ModifiersNote, { lineUnitPrice } from 'components/Modules/ModifiersNote';
import { productPath } from 'utils/storePaths';

const Cart = () => {
  const { cartList, setCartList, setSnackbar } = useContext(DataContext);
  const navigate = useNavigate();
  const [shipping, setShipping] = useState(() => {
    const saved = CartService.getShippingSelection();
    const normalized = CartService.normalizeShippingPkr(saved.amount);
    return String(normalized === 0 ? 0 : PKR_STANDARD_SHIPPING);
  });
  const [shippingMethod, setShippingMethod] = useState(() => {
    const saved = CartService.getShippingSelection();
    return saved.amount === 0 ? 'free' : 'standard';
  });
  const [cartTotals, setCartTotals] = useState({
    subtotal: 0,
    shipping: 0,
    tax: 0,
    taxRate: 0.08,
    total: 0,
  });
  // Per-store breakdown (CLAUDE.md step D17b) -- [{store_id, store_name,
  // subtotal, shipping, tax, total}], straight from /checkout/quote. Never
  // computed on the client.
  const [storeBreakdown, setStoreBreakdown] = useState([]);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);

  useEffect(() => {
    if (!cartList || cartList.length === 0) return;

    // Client-side numbers are only the loading/error fallback until the
    // server quote resolves -- the grand total shown to the user always
    // comes from /checkout/quote.
    const fallback = CartService.calculateCartTotals(cartList, shipping);
    setCartTotals((prev) => ({ ...fallback, taxRate: prev.taxRate ?? 0.08 }));

    if (!AuthService.isAuthenticated()) return;

    let cancelled = false;
    CartService.getCheckoutQuote(shippingMethod).then((quote) => {
      if (cancelled || !quote.success) return;
      setCartTotals({
        subtotal: quote.subtotal,
        shipping: quote.shipping,
        tax: quote.tax,
        taxRate: quote.taxRate ?? 0.08,
        total: quote.total,
      });
      setStoreBreakdown(quote.stores);
    });
    return () => {
      cancelled = true;
    };
  }, [cartList, shipping, shippingMethod]);

  // Group cart items by store for display (store name comes from the
  // quote's breakdown; falls back to a generic label until it resolves).
  const groupedByStore = React.useMemo(() => {
    const groups = new Map();
    for (const item of cartList) {
      const storeId = item.product?.store_id ?? 'unknown';
      if (!groups.has(storeId)) groups.set(storeId, []);
      groups.get(storeId).push(item);
    }
    const storeNameById = new Map(storeBreakdown.map((s) => [s.store_id, s.store_name]));
    const totalsById = new Map(storeBreakdown.map((s) => [s.store_id, s]));
    return Array.from(groups.entries()).map(([storeId, items]) => ({
      storeId,
      storeName: storeNameById.get(storeId) || (storeId === 'unknown' ? t('cart.store') : t('vendorOrder.storeN', { id: storeId })),
      items,
      totals: totalsById.get(storeId) || null,
    }));
  }, [cartList, storeBreakdown]);

  const qualifiesFreeShipping = cartTotals.subtotal >= PKR_FREE_SHIPPING_THRESHOLD;

  useEffect(() => {
    if (!cartList?.length) return;
    if (!qualifiesFreeShipping && shipping === '0') {
      const value = String(PKR_STANDARD_SHIPPING);
      setShipping(value);
      setShippingMethod('standard');
      CartService.saveShippingSelection('standard', value);
      return;
    }
    if (qualifiesFreeShipping && shipping !== '0') {
      const value = '0';
      setShipping(value);
      setShippingMethod('free');
      CartService.saveShippingSelection('free', value);
    }
  }, [cartList?.length, qualifiesFreeShipping, shipping]);

  const handleClick = (event) => {
    event.preventDefault();
    navigate(event.target.pathname);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    if (value === '0' && !qualifiesFreeShipping) return;
    setShipping(value);
    let method = 'standard';
    if (value === '0') method = 'free';
    setShippingMethod(method);
    CartService.saveShippingSelection(method, value);
  };

  const deleteFromCart = async (event) => {
    const cartItemId = Number(event.currentTarget.id);
    setRemovingItemId(cartItemId);
    const result = await CartService.removeFromCart(cartItemId);

    if (result.success) {
      if (AuthService.isAuthenticated()) {
        const cartResult = await CartService.getCart();
        if (cartResult.success) {
          setCartList(cartResult.cartItems);
        }
      } else {
        setCartList(CartService.loadLocalCart());
      }
      setSnackbar({
        ...handleApiSuccess(t('cart.removed')),
        open: true,
      });
    } else {
      setSnackbar({
        ...handleApiError(result.error),
        open: true,
      });
    }
    setRemovingItemId(null);
  };

  const changeAmount = async (amount, cartItemId) => {
    const cartItem = cartList.find((item) => item.id === cartItemId);
    if (!cartItem) return;

    const newQuantity = cartItem.quantity + amount;
    if (newQuantity < 1) return;

    setUpdatingItemId(cartItemId);
    const result = await CartService.updateCartItem(cartItemId, newQuantity);

    if (result.success) {
      if (AuthService.isAuthenticated()) {
        const cartResult = await CartService.getCart();
        if (cartResult.success) {
          setCartList(cartResult.cartItems);
        }
      } else {
        setCartList(CartService.loadLocalCart());
      }
    } else {
      setSnackbar({
        ...handleApiError(result.error),
        open: true,
      });
    }
    setUpdatingItemId(null);
  };

  const subTotal = (status) => {
    if (cartTotals.subtotal) {
      return status === 'formatted' ? formatPrice(cartTotals.subtotal) : cartTotals.subtotal;
    }

    let total = 0;
    cartList.forEach((item) => {
      total += Number(lineUnitPrice(item) * item.quantity);
    });
    return status === 'formatted' ? formatPrice(total) : total;
  };

  const checkOut = () => {
    if (!cartList || cartList.length === 0) return;
    CartService.saveShippingSelection(shippingMethod, shipping);
    navigate('/checkout');
  };

  const inputSpinNone =
    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

  return (
    <>
      {cartList.length === 0 ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-neutral-700/40 to-white/50 px-4">
          <div className="w-full max-w-lg rounded-3xl border border-amber-100 bg-white px-10 py-12 text-center shadow-xl sm:px-14">
            <h2 className="mb-3 text-2xl font-semibold text-neutral-800 sm:text-[28px]">{t('cart.emptyTitle')}</h2>
            <p className="mb-6 text-sm text-neutral-600 sm:text-[15px]">{t('cart.emptyBody')}</p>
            <button
              type="button"
              onClick={() => navigate('/shop')}
              className="rounded-full border-none bg-primary px-7 py-3.5 text-sm font-bold text-on-accent shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              {t('cart.continue')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className="theme-light flex flex-col items-center justify-center bg-neutral-200 bg-cover bg-center bg-no-repeat py-11"
            style={{
              backgroundImage: "url('/images/banners/banner-tech1.webp')",
            }}
          >
            <h5 className="text-[40px] font-normal text-neutral-800">{t('cart.title')}</h5>
            <h6 className="text-xl font-normal text-primary">{t('nav.shop')}</h6>
          </div>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <nav className="mb-0 px-2.5 py-3.5" aria-label={t('common.breadcrumb')}>
              <ol className="flex flex-wrap items-center gap-1 text-sm" role="presentation" onClick={handleClick}>
                <li>
                  <a
                    href="/"
                    className="text-neutral-700 no-underline transition-colors duration-200 hover:text-primary"
                  >
                    {t('nav.home')}
                  </a>
                </li>
                <li className="flex items-center gap-0.5 text-neutral-400">
                  <MdNavigateNext className="text-base rtl:-scale-x-100" aria-hidden />
                  <a
                    href="/wishlist"
                    aria-current="page"
                    className="font-medium text-neutral-900 no-underline"
                  >
                    {t('cart.title')}
                  </a>
                </li>
              </ol>
            </nav>
          </div>
          <hr className="mb-10 mt-0 border-0 border-b border-neutral-200" />
          <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
              <div className="lg:col-span-8">
                <table className="w-full border-collapse text-neutral-900 max-lg:[&_thead]:hidden">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('wishlist.product')}</th>
                      <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('wishlist.price')}</th>
                      <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('cart.quantity')}</th>
                      <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('cart.total')}</th>
                      <th className="py-3.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByStore.map((group) => (
                      <React.Fragment key={group.storeId}>
                        {groupedByStore.length > 1 && (
                          <tr className="bg-neutral-50">
                            <td colSpan={5} className="px-1 py-2.5 text-sm font-semibold text-neutral-700">
                              🏬 {group.storeName}
                              {group.totals && (
                                <span className="ms-2 font-normal text-neutral-500">
                                  {t('cart.groupLine', { subtotal: formatPrice(group.totals.subtotal), shipping: group.totals.shipping === 0 ? t('cart.free') : formatPrice(group.totals.shipping) })}
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                        {group.items.map((item) => (
                          <tr
                            key={item.id}
                            className="relative border-b border-neutral-200 max-lg:mb-4 max-lg:flex max-lg:flex-col max-lg:border max-lg:border-neutral-300 max-lg:py-10 max-lg:px-2"
                          >
                            <td className="max-lg:w-full">
                              <div className="flex max-lg:flex-col max-lg:items-center">
                                <div className="me-7 block h-20 w-20 shrink-0 max-lg:mx-auto max-lg:mb-3">
                                  <img
                                    src={getPrimaryProductImageSrc(item.product || {})}
                                    alt={t('product.productLower')}
                                    referrerPolicy="no-referrer"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                                <Link
                                  to={productPath(item.product)}
                                  className="inline-block max-w-[300px] truncate text-base font-normal text-neutral-800 no-underline max-lg:max-w-[85%] max-lg:whitespace-normal max-lg:text-center"
                                >
                                  {item.product?.title || item.product?.name}
                                </Link>
                                <ModifiersNote modifiers={item.modifiers} invalid={item.modifiers_invalid} variant={item.variant_label} variantInvalid={item.variant_invalid} booking={item.booking} />
                              </div>
                            </td>
                            <td>
                              <h4 className="w-[120px] text-base font-normal max-lg:mx-auto max-lg:my-1 max-lg:text-center">
                                {formatPrice(lineUnitPrice(item))}
                                {item.deal && (
                                  <span className="ms-2 text-sm text-slate-500 line-through">
                                    {formatPrice(Number(item.deal.original_price) + lineUnitPrice(item) - Number(item.deal.deal_price))}
                                  </span>
                                )}
                              </h4>
                            </td>
                            <td className="w-[135px] max-lg:mb-1 max-lg:w-full">
                              <div className="flex w-[100px] border border-neutral-300 max-lg:w-full">
                                <button
                                  type="button"
                                  disabled={updatingItemId === item.id}
                                  onClick={() => changeAmount(-1, item.id)}
                                  className="flex flex-1 items-center justify-center border-none bg-transparent px-2 py-3 text-neutral-400 transition-colors hover:text-primary"
                                >
                                  <HiMinus />
                                </button>
                                <input
                                  type="number"
                                  min={1}
                                  value={item.quantity}
                                  readOnly
                                  className={`w-full border-0 bg-transparent py-2 text-center text-sm font-light text-neutral-600 outline-none ${inputSpinNone}`}
                                />
                                <button
                                  type="button"
                                  disabled={updatingItemId === item.id}
                                  onClick={() => changeAmount(1, item.id)}
                                  className="flex flex-1 items-center justify-center border-none bg-transparent px-2 py-3 text-neutral-400 transition-colors hover:text-primary"
                                >
                                  <HiPlus />
                                </button>
                              </div>
                            </td>
                            <td className="w-20 text-base font-normal text-primary max-lg:text-center">
                              {formatPrice(lineUnitPrice(item) * item.quantity)}
                            </td>
                            <td>
                              <div
                                role="button"
                                tabIndex={0}
                                className={`flex w-10 cursor-pointer items-center justify-end text-neutral-300 max-lg:absolute max-lg:end-6 max-lg:top-2 max-lg:w-auto max-lg:justify-center max-lg:p-2 hover:text-neutral-800 ${removingItemId === item.id ? 'pointer-events-none opacity-50' : ''}`}
                                onClick={deleteFromCart}
                                onKeyDown={(e) => e.key === 'Enter' && deleteFromCart(e)}
                                id={item.id}
                              >
                                <MdClose className="text-xl transition-colors" aria-hidden />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded border border-dashed border-neutral-300 bg-neutral-50 px-6 pb-8 pt-6">
                  <div>
                    <h5 className="border-b border-neutral-400 pb-4 text-base font-medium">{t('cart.cartTotal')}</h5>
                    <p className="flex h-[70px] items-center justify-between border-b border-neutral-200">
                      {t('cart.subtotalColon')} <span>{subTotal('formatted')}</span>
                    </p>
                  </div>
                  <div>
                    <p className="pb-3 pt-5 text-neutral-800">{t('cart.shippingColon')}</p>
                  </div>
                  <fieldset className="border-0 border-b border-solid border-neutral-200 pb-6">
                    <legend className="sr-only">{t('cart.shippingMethod')}</legend>
                    <div className="space-y-0">
                      <label className="flex cursor-pointer items-start gap-2 py-2">
                        <input
                          type="radio"
                          name="shipping"
                          value="0"
                          checked={shipping === '0'}
                          onChange={handleChange}
                          disabled={!qualifiesFreeShipping}
                          className="mt-1 h-4 w-4 shrink-0 accent-amber-600 disabled:opacity-50"
                        />
                        <span className="flex w-full justify-between text-sm font-light text-neutral-800">
                          <span className="flex flex-col gap-0.5">
                            <span>{t('home.trust.shipping.title')}</span>
                            <span className="text-xs text-neutral-500">
                              {t('cart.freeOver', { amount: formatPrice(PKR_FREE_SHIPPING_THRESHOLD) })}
                            </span>
                          </span>
                          <span>{formatPrice(0)}</span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-2 py-2">
                        <input
                          type="radio"
                          name="shipping"
                          value={String(PKR_STANDARD_SHIPPING)}
                          checked={shipping === String(PKR_STANDARD_SHIPPING)}
                          onChange={handleChange}
                          className="mt-1 h-4 w-4 shrink-0 accent-amber-600"
                        />
                        <span className="flex w-full justify-between text-sm font-light text-neutral-800">
                          <span>{t('cart.standard')}</span>
                          <span>{formatPrice(PKR_STANDARD_SHIPPING)}</span>
                        </span>
                      </label>
                    </div>
                  </fieldset>
                  <div>
                    <p className="flex h-[70px] items-center justify-between text-base text-primary">
                      {t('cart.tax', { rate: Math.round((cartTotals.taxRate ?? 0.08) * 100) })}
                      <span>{formatPrice(cartTotals.tax || 0)}</span>
                    </p>
                    <p className="flex h-[70px] items-center justify-between text-base text-primary">
                      {t('cart.total')}
                      <span>{formatPrice(cartTotals.total || 0)}</span>
                    </p>
                    <button
                      type="button"
                      className="h-11 w-full rounded-xl border-none bg-primary text-sm font-bold tracking-wide text-on-accent shadow transition hover:-translate-y-px hover:bg-[#b16e40] hover:shadow-md disabled:cursor-not-allowed disabled:bg-[#d9b18e] disabled:opacity-55"
                      disabled={!cartList.length}
                      onClick={checkOut}
                    >
                      {cartList.length === 0 ? t('cart.continueLower') : t('cart.proceed')}
                    </button>
                  </div>
                </div>
                <Link
                  to="/"
                  className="mt-8 flex w-full items-center justify-center gap-2 border border-neutral-200 bg-transparent px-4 py-2.5 text-sm text-neutral-800 no-underline transition hover:border-primary hover:bg-amber-50"
                >
                  {t('cart.continueUpper')} <TbRefresh />
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
      <hr className="m-0 block h-px border-0 border-t border-neutral-300 p-0" />
    </>
  );
};

export default Cart;
